import PropTypes from 'prop-types';
import { useEffect } from 'react';

/**
 * On-screen keyboard component with QWERTY layout
 * Shows letter status matching the game board colors
 * Allows input via clicking keys
 */
const Keyboard = ({ onKeyPress, gameState }) => {
  const { targetWord, guesses } = gameState || {};

  // Handle real keyboard input
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Ignore if focused on an input/textarea/select or contenteditable
      const target = event.target;
      const tagName =
        target && target.tagName ? target.tagName.toLowerCase() : '';
      const isFormField =
        tagName === 'input' || tagName === 'textarea' || tagName === 'select';
      const isContentEditable = target && target.isContentEditable;
      if (isFormField || isContentEditable) return;

      // Ignore modifier key combos
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const key = event.key;

      if (key === 'Enter') {
        onKeyPress('ENTER');
        return;
      }

      if (key === 'Backspace' || key === 'Delete') {
        onKeyPress('BACKSPACE');
        return;
      }

      // For letters A-Z
      if (key && key.length === 1 && /[a-zA-Z]/.test(key)) {
        onKeyPress(key.toUpperCase());
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onKeyPress]);

  // Define keyboard layout
  const keyboardRows = [
    ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
    ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
    ['ENTER', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', 'BACKSPACE'],
  ];

  // Calculate letter states from all guesses
  const getLetterState = (letter) => {
    if (!targetWord || !guesses) return '';

    const normalizedLetter = String(letter).toLowerCase();
    const normalizedTarget = String(targetWord).toLowerCase();

    // Default state if letter hasn't been used
    let state = '';

    // Check all guesses to determine letter state
    for (const guess of guesses) {
      const normalizedGuess = String(guess).toLowerCase();
      for (let i = 0; i < normalizedGuess.length; i++) {
        const guessLetter = normalizedGuess[i];

        // Skip placeholders or non-letters
        if (guessLetter === '_' || !/[a-z]/.test(guessLetter)) continue;

        // Skip this iteration if not the letter we're checking
        if (guessLetter !== normalizedLetter) continue;

        // Letter is in the correct position
        if (guessLetter === normalizedTarget[i]) {
          return 'correct'; // Highest precedence, return immediately
        }

        // Letter is in the word but wrong position
        if (normalizedTarget.includes(guessLetter)) {
          state = 'present'; // Set state but keep checking for correct
        } else if (state === '') {
          // Only set to absent if no other state has been assigned yet
          state = 'absent';
        }
      }
    }

    return state;
  };

  const handleKeyClick = (key) => {
    onKeyPress(key);
  };

  return (
    <div className="keyboard">
      {keyboardRows.map((row, rowIndex) => (
        <div key={rowIndex} className="keyboard-row">
          {row.map((key) => {
            // Special classes for special keys
            const backSpaceKey = key === 'BACKSPACE';
            const isSpecialKey = key === 'ENTER'; // || key === 'BACKSPACE';
            const letterState =
              !isSpecialKey || backSpaceKey ? getLetterState(key) : '';

            return (
              <button
                key={key}
                className={`keyboard-key ${letterState} ${isSpecialKey ? 'special-key' : ''}`}
                onClick={() => handleKeyClick(key)}
                aria-label={key}
              >
                {key === 'BACKSPACE' ? '⌫' : key}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

Keyboard.propTypes = {
  onKeyPress: PropTypes.func.isRequired,
  gameState: PropTypes.shape({
    targetWord: PropTypes.string,
    guesses: PropTypes.arrayOf(PropTypes.string),
  }),
};

export default Keyboard;
